/**
 * canvasLetterhead — PROTOTYPE (étude de faisabilité, 2026-08-19).
 *
 * Pose le papier à en-tête du cabinet dans le canvas SuperDoc, en **vrai en-tête
 * Word de première page** (`w:headerReference w:type="first"` + `w:titlePg`), et
 * non en bloc de corps.
 *
 * ## Ce qui a été prouvé avant d'écrire une ligne
 *
 * `superdoc@2.6.0` expose bien l'écriture dans un en-tête de section : le
 * Document API de l'éditeur (`editor.doc`) porte `sections.setTitlePage`,
 * `headerFooters.parts.create`, `headerFooters.refs.set` et un `create.image`
 * qui accepte une **story** d'en-tête (`in: { storyType: 'headerFooterPart' }`).
 * Ce n'est pas une lecture de typages : les quatre opérations ont été jouées
 * dans Chromium contre le VRAI gabarit `/templates/blank-with-page-numbers.docx`
 * (sonde `apps/legalworkspace/letterhead-probe.html`), le logo est apparu en
 * page 1 et pas en page 2, et le `.docx` réexporté contient bien
 * `<w:headerReference w:type="first" r:id="rId9"/><w:titlePg/>`,
 * `word/header1.xml`, son `.rels` et `word/media/image1.png`.
 *
 * Conséquence pour le gabarit : il n'a RIEN à porter. `parts.create` fabrique
 * `word/header1.xml` à la volée, donc `generate-blank-template.ts` reste
 * inchangé — et la garde de parité #989, qui lit le `.docx` commité, n'est pas
 * concernée.
 *
 * ## Invariants produit (les mêmes que les 3 autres rendus)
 *
 *  - **Opt-in strict** : `settings.enabled === false` ⇒ on ne touche à rien.
 *  - **Première page uniquement** : l'image va dans la variante `first`, et
 *    `titlePg` est levé. Un en-tête sur toutes les pages serait une régression.
 *  - **Best-effort** : toute anomalie rend un motif d'abandon, jamais une
 *    exception. Un canvas sans logo est un défaut cosmétique ; un canvas qui ne
 *    s'ouvre pas est une panne.
 *  - **Géométrie partagée** : la taille sort de `fitBordereauCoverLetterhead`,
 *    comme l'add-in Word et les deux bordereaux. Deux calculs séparés feraient
 *    sortir le même logo à deux tailles selon le document.
 *
 * ## Deux appelants, deux régimes (2026-08-19)
 *
 * 1. **La pose automatique** (`RedactionCanvas`, au montage d'un canvas vierge)
 *    est NON DESTRUCTIVE et silencieuse : un en-tête déjà présent la fait
 *    renoncer (`'en-tete-deja-present'`). C'est le comportement d'origine, et il
 *    ne bouge pas.
 * 2. **Le geste de l'avocat** (`appliquerLEnTeteDuCabinet`, bouton de la barre
 *    d'outils) est RÉTROACTIF : il marche sur un brouillon rouvert comme sur un
 *    `.docx` importé, et il peut REMPLACER l'en-tête en place — mais seulement
 *    après une confirmation explicite, parce que la garde d'idempotence est
 *    anonyme (voir `lireLEnTeteDePremierePage`) et que l'en-tête écrasé peut
 *    être celui que l'avocat a lui-même mis dans son Word.
 */

import { fitBordereauCoverLetterhead, type LetterheadAlignment } from '@hector/shared-types';

/**
 * Ce que la pose lit RÉELLEMENT des réglages d'un en-tête — rien de plus.
 *
 * Élargi depuis `LetterheadSettings` le 2026-09-03, quand le canvas a dû pouvoir poser un
 * en-tête NOMMÉ choisi par l'avocat. Un en-tête nommé arrive de `GET …/letterheads`, qui
 * n'expose délibérément NI `filePath` NI `mimeType` (clé S3 privée). Exiger `LetterheadSettings`
 * aurait donc obligé l'appelant à fabriquer une fausse clé pour satisfaire un champ que ce
 * module ne lit pas — un mensonge dans le type, à un endroit où le lecteur suivant chercherait
 * forcément à quoi sert ce chemin.
 *
 * `LetterheadSettings` reste assignable à cette forme : le chemin par défaut est inchangé.
 */
export interface ReglagesDePose {
  readonly enabled: boolean;
  /** `null` = aucun fichier. Seule `widthMm` est lue — les octets arrivent par `imageBlob`. */
  readonly image: { readonly widthMm?: number } | null;
  readonly alignment: LetterheadAlignment;
}

/** Les réglages d'un en-tête ET ses octets — ce qu'il faut pour en poser un, quel qu'il soit. */
export interface EnTeteAPoser {
  readonly settings: ReglagesDePose | null;
  readonly imageBlob: Blob | null;
}
import { PAGE_MARGIN_MM } from '../styleSpec';

/** Largeur A4 en millimètres — ECMA-376 §17.6.13 (`w:pgSz` du gabarit : 11906 twips). */
const A4_WIDTH_MM = 210;

/**
 * Largeur utile du canvas : A4 moins les marges du gabarit (20 mm, cf. `styleSpec`).
 *
 * Une source pour l'en-tête du cabinet et pour une image posée dans le corps
 * (`insert_image`, `canvas-agent/imageActionExecutors.ts`) : une image plus
 * large que le texte se ramène à cette largeur. Elle vaut pour la page du
 * gabarit ; un acte dont l'avocat a changé les marges ou l'orientation n'est
 * pas relu ici.
 */
export const LARGEUR_UTILE_DU_CANVAS_MM = A4_WIDTH_MM - PAGE_MARGIN_MM * 2;

/** Millimètres → points : `create.image` prend une taille en `pt` (`ImageSize.unit`). */
const MM_TO_PT = 72 / 25.4;

/**
 * Story OOXML ciblée par une écriture du Document API. Seules les deux formes
 * utilisées ici sont décrites — le contrat complet vit dans
 * `superdoc/dist/document-api/src/types/story.types.d.ts`.
 */
type StoryDEnTete = { kind: 'story'; storyType: 'headerFooterPart'; refId: string };

/** Adresse de section rendue par `sections.list()`. */
interface AdresseDeSection {
  kind: 'section';
  sectionId: string;
}

/**
 * Adresse du slot « en-tête de première page » d'une section — la cible que
 * `refs.set` remplit et que `refs.clear` détache. Les deux opérations visent
 * exactement la même adresse : la nommer une fois évite qu'un remplacement
 * détache un slot et en remplisse un autre.
 */
interface AdresseDuSlotDePremierePage {
  kind: 'headerFooterSlot';
  section: AdresseDeSection;
  headerFooterKind: 'header';
  variant: 'first';
}

/**
 * Adresse de bloc rendue par `blocks.list()`, COMPLÉTÉE de sa story.
 *
 * `blocks.list({ in: story })` ne répète pas la story dans chaque entrée : une
 * `BlockListEntry` ne porte que `nodeId` / `nodeType`. Or le Document API lit
 * l'absence de `story` comme « ce bloc est dans le CORPS » — `BlockNodeAddress`
 * déclare `story?: StoryLocator` avec le commentaire « Omit for body ». Une
 * adresse reconstruite sans elle vise donc le corps, où le nodeId du logo
 * n'existe pas : la mutation rend un reçu `{ success: false }` au lieu de jeter,
 * et l'échec passe inaperçu.
 *
 * C'est exactement ce qui a fait perdre l'alignement du papier à en-tête
 * (constaté le 2026-08-24 : logo collé à gauche alors que le réglage cabinet
 * disait `center`, et aperçu des Paramètres correctement centré — l'écart entre
 * les deux surfaces était le symptôme). La suppression du paragraphe vide de
 * tête échouait de la même façon, pour la même raison.
 */
interface AdresseDeBloc {
  kind: string;
  nodeType: string;
  nodeId: string;
  story?: StoryDEnTete;
}

/**
 * Sous-ensemble « en-tête » du Document API. Toutes les surfaces sont
 * optionnelles, comme dans les autres modules `canvas*` du canvas : une version
 * du paquet qui n'exposerait pas l'une d'elles doit produire un abandon nommé,
 * jamais un `TypeError` au milieu de l'ouverture d'un acte.
 */
export interface DocumentApiEnTete {
  sections?: {
    list?: () => unknown;
    setTitlePage?: (entree: { target: AdresseDeSection; enabled: boolean }) => unknown;
  };
  headerFooters?: {
    list?: (query: { kind: 'header' }) => unknown;
    /**
     * Résoud l'en-tête effectif d'un slot, y compris quand il est hérité de la
     * variante `default`. `list` ne rend que les références EXPLICITES, donc un
     * document avec un en-tête sur `default` uniquement (toutes les pages) est
     * invisible de `lireLEnTeteDePremierePage` : `resolve` lève l'ambiguïté.
     *
     * Signature d'après `header-footers.d.ts` (superdoc@2.6.0) :
     * `resolve(input: { target: HeaderFooterSlotAddress }): HeaderFooterResolveResult`
     * Résultats : `{ status: 'explicit'; refId }` / `{ status: 'inherited'; refId; ... }`
     * / `{ status: 'none' }`.
     */
    resolve?: (entree: { target: AdresseDuSlotDePremierePage }) => unknown;
    parts?: {
      create?: (entree: { kind: 'header' }) => unknown;
      /**
       * Ménage seulement — `HeaderFooterPartEntry.referencedBySections` dit qui
       * référence encore une partie détachée. Absente d'une version du paquet,
       * on renonce à supprimer plutôt que de supprimer à l'aveugle.
       */
      list?: (query: { kind: 'header' }) => unknown;
      delete?: (entree: { target: { kind: 'headerFooterPart'; refId: string } }) => unknown;
    };
    refs?: {
      set?: (entree: { target: AdresseDuSlotDePremierePage; refId: string }) => unknown;
      clear?: (entree: { target: AdresseDuSlotDePremierePage }) => unknown;
    };
  };
  create?: {
    image?: (entree: {
      in: StoryDEnTete;
      src: string;
      alt?: string;
      size: { width: number; height: number; unit: 'pt' };
    }) => unknown;
  };
  blocks?: {
    list?: (entree: { in: StoryDEnTete; includeText: true }) => unknown;
    delete?: (entree: { target: AdresseDeBloc }) => unknown;
  };
  format?: {
    paragraph?: {
      setAlignment?: (entree: { target: AdresseDeBloc; alignment: 'left' | 'center' | 'right' }) => unknown;
    };
  };
}

/**
 * Pourquoi la pose n'a pas eu lieu, ou qu'elle a eu lieu. Une VALEUR et pas une
 * exception : l'appelant journalise, il n'a rien à rattraper.
 */
export type ResultatDeLEnTete =
  | 'pose'
  /**
   * Un en-tête était là, il a été retiré et celui du cabinet a pris sa place.
   * Distinct de `'pose'` parce que ce n'est pas la même phrase à dire à
   * l'avocat : « ajouté » et « remplacé » ne décrivent pas le même geste, et
   * seul le module sait lequel a réellement eu lieu. Inatteignable sans
   * `remplacerLEnTeteExistant`.
   */
  | 'remplace'
  | 'desactive'
  | 'sans-image'
  | 'en-tete-deja-present'
  | 'api-indisponible'
  | 'image-illisible'
  | 'refus-du-moteur';

function estUnObjet(valeur: unknown): valeur is Record<string, unknown> {
  return typeof valeur === 'object' && valeur !== null;
}

function receiptEstEnSucces(valeur: unknown): boolean {
  return estUnObjet(valeur) && valeur.success === true;
}

/** `DiscoveryOutput<T>` du Document API : `{ items: T[] }`. */
function lireLesItems(valeur: unknown): readonly Record<string, unknown>[] {
  if (!estUnObjet(valeur)) return [];
  const items = valeur.items;
  if (!Array.isArray(items)) return [];
  return items.filter(estUnObjet);
}

function lirePremiereAdresseDeSection(valeur: unknown): AdresseDeSection | null {
  const premiere = lireLesItems(valeur)[0];
  const adresse = premiere?.address;
  if (!estUnObjet(adresse) || typeof adresse.sectionId !== 'string') return null;
  return { kind: 'section', sectionId: adresse.sectionId };
}

/**
 * L'en-tête de première page DÉJÀ référencé sur CETTE section, avec son `refId`.
 *
 * Un seul juge pour les deux régimes : la pose automatique s'en sert pour NE PAS
 * écraser (même garde d'idempotence que l'add-in Word — sans elle, une seconde
 * ouverture poserait un second logo), le remplacement pour savoir quoi détacher.
 * Deux lectures avec deux règles finiraient par répondre deux choses différentes
 * du même document.
 *
 * ⚠️ **La garde est ANONYME**, et c'est structurel : `HeaderFooterSlotEntry` ne
 * porte aucune provenance. Un logo importé avec le `.docx` de l'avocat et un
 * logo posé par Hector à l'ouverture précédente sont ici indiscernables. C'est
 * la raison d'être de la confirmation dans `appliquerLEnTeteDuCabinet` : le
 * module ne peut pas décider seul qu'un écrasement est sans conséquence.
 *
 * La lecture est filtrée sur la section visée : `refs.set` écrit dans le slot
 * d'UNE section, donc un en-tête vu ailleurs ne répond pas à la question posée.
 * Une entrée dont la section est illisible est comptée comme nôtre — sur un
 * doute, mieux vaut renoncer à poser que d'empiler un second logo.
 */
function lireLEnTeteDePremierePage(valeur: unknown, section: AdresseDeSection): { refId: string } | null {
  for (const entree of lireLesItems(valeur)) {
    if (entree.variant !== 'first' || entree.isExplicit !== true) continue;
    if (typeof entree.refId !== 'string') continue;
    const sectionDeLEntree = estUnObjet(entree.section) ? entree.section.sectionId : undefined;
    if (typeof sectionDeLEntree === 'string' && sectionDeLEntree !== section.sectionId) continue;
    return { refId: entree.refId };
  }
  return null;
}

/**
 * Lit le statut de résolution rendu par `headerFooters.resolve`. L'API est
 * synchrone en superdoc@2.6.0, mais les appelants l'`await` comme les autres
 * surfaces pour rester robustes à un changement de nature du provider.
 *
 * Un `null` signifie « la réponse était illisible » — assimilé à l'absence de
 * surface, pas à une réponse négative : l'absence d'information ne doit jamais
 * interdire une action qui serait autrement légitime.
 */
function lireLeStatutDeResolution(valeur: unknown): 'explicit' | 'inherited' | 'none' | null {
  if (!estUnObjet(valeur)) return null;
  const { status } = valeur;
  if (status === 'explicit' || status === 'inherited' || status === 'none') return status;
  return null;
}

/**
 * Détache l'en-tête de première page de la section, et fait le ménage de la
 * partie OOXML devenue orpheline.
 *
 * Deux gestes, deux niveaux d'exigence :
 *  - `refs.clear` **conditionne** le remplacement. Tant que le slot pointe
 *    l'ancienne partie, un `refs.set` qui échouerait laisserait l'ancien logo à
 *    l'écran alors que l'avocat vient d'accepter un remplacement.
 *  - `parts.delete` n'est qu'un ménage : une partie que plus aucune section ne
 *    référence n'est peinte nulle part et ne pèse que quelques octets dans le
 *    `.docx`. Son échec ne compromet rien, donc il ne fait pas échouer la pose.
 *
 * ⚠️ **La suppression est conditionnée à l'orphelinat RELU**
 * (`referencedBySections` vide après le détachement). Une même partie peut être
 * référencée par plusieurs sections d'un `.docx` importé : la supprimer sans
 * vérifier viderait l'en-tête d'une section que l'avocat n'a jamais demandé de
 * toucher — une perte silencieuse, dans son propre document. Sans `parts.list`
 * pour trancher, on ne supprime pas.
 *
 * @returns `true` si le slot est bien détaché — seul prérequis d'une re-pose.
 */
async function retirerLEnTeteDePremierePage(
  headerFooters: NonNullable<DocumentApiEnTete['headerFooters']>,
  slot: AdresseDuSlotDePremierePage,
  refId: string,
): Promise<boolean> {
  if (!headerFooters.refs?.clear) return false;
  if (!receiptEstEnSucces(await headerFooters.refs.clear({ target: slot }))) return false;

  const parts = headerFooters.parts;
  if (!parts?.list || !parts.delete) return true;

  const partie = lireLesItems(await parts.list({ kind: 'header' })).find((entree) => entree.refId === refId);
  const referencesRestantes = partie?.referencedBySections;
  // Une partie introuvable dans la liste est déjà partie avec le détachement ;
  // une liste de références illisible (ni tableau, ni vide) laisse le doute, et
  // le doute ne supprime pas.
  if (!Array.isArray(referencesRestantes) || referencesRestantes.length > 0) return true;

  await parts.delete({ target: { kind: 'headerFooterPart', refId } });
  return true;
}

function lireLeRefIdCree(valeur: unknown): string | null {
  if (!receiptEstEnSucces(valeur) || !estUnObjet(valeur)) return null;
  return typeof valeur.refId === 'string' ? valeur.refId : null;
}

function lireLesBlocs(valeur: unknown): readonly Record<string, unknown>[] {
  if (!estUnObjet(valeur)) return [];
  const blocs = valeur.blocks;
  if (!Array.isArray(blocs)) return [];
  return blocs.filter(estUnObjet);
}

function lireLAdresseDunBloc(bloc: Record<string, unknown>, story: StoryDEnTete): AdresseDeBloc | null {
  const adresse = estUnObjet(bloc.address) ? bloc.address : bloc;
  if (typeof adresse.nodeId !== 'string' || typeof adresse.nodeType !== 'string') return null;
  return {
    kind: typeof adresse.kind === 'string' ? adresse.kind : 'block',
    nodeType: adresse.nodeType,
    nodeId: adresse.nodeId,
    story,
  };
}

/**
 * Code d'échec d'un reçu de mutation, ou `null` si la mutation a réussi.
 *
 * `NO_OP` en fait partie : le Document API classe « rien à faire » comme un
 * échec (`ReceiptFailureCode`). Aligner à gauche un paragraphe déjà à gauche
 * rend donc `NO_OP` — un non-événement qu'il ne faut surtout pas journaliser
 * comme une anomalie, sinon le cas le plus courant devient le plus bruyant.
 */
function lireLeCodeDEchec(valeur: unknown): string | null {
  if (receiptEstEnSucces(valeur)) return null;
  if (!estUnObjet(valeur)) return 'REPONSE_ILLISIBLE';
  const echec = estUnObjet(valeur.failure) ? valeur.failure : null;
  return typeof echec?.code === 'string' ? echec.code : 'ECHEC_SANS_CODE';
}

/**
 * PNG/JPEG → data URI. `create.image` REFUSE tout ce qui n'est pas une data URI
 * (message du moteur : « create.image currently requires a base64 data URI. »),
 * contrairement à l'export bordereau qui, lui, passe un `Uint8Array` à `docx`.
 */
async function versDataUri(blob: Blob): Promise<string> {
  return await new Promise<string>((resolve, reject) => {
    const lecteur = new FileReader();
    lecteur.onload = () => {
      const resultat = lecteur.result;
      if (typeof resultat === 'string') resolve(resultat);
      else reject(new Error("canvasLetterhead : FileReader n'a pas rendu une data URI"));
    };
    lecteur.onerror = () => reject(lecteur.error ?? new Error('canvasLetterhead : lecture du blob en échec'));
    lecteur.readAsDataURL(blob);
  });
}

/** Réglage de la pose — une seule décision, et elle est explicite. */
export interface OptionsDePose {
  /**
   * Remplacer un en-tête de première page déjà présent, au lieu de renoncer.
   *
   * Absent par défaut, et ce défaut est l'invariant du montage : la pose
   * automatique doit rester incapable de détruire quoi que ce soit, même le
   * jour où un appelant l'oublie. Le forçage ne se lit donc que d'un appelant
   * qui l'a écrit — c'est-à-dire, aujourd'hui, d'un geste que l'avocat a
   * confirmé (`appliquerLEnTeteDuCabinet`).
   */
  readonly remplacerLEnTeteExistant?: boolean;
}

/**
 * Pose l'en-tête du cabinet en première page du canvas.
 *
 * Best-effort : ne lève jamais, rend un motif. Les deux finitions capables de
 * lever une fois l'en-tête posé (lecture de l'image, mise en page) sont
 * encadrées — un logo effectivement visible ne doit pas être annoncé en échec.
 *
 * @param doc - `editor.doc`, le Document API de l'éditeur SuperDoc monté.
 * @param entree - les réglages du cabinet et les OCTETS de l'image, déjà
 *   récupérés en même-origine (`GET /api/cabinet-settings/:id/letterhead/image` —
 *   le seau OVH n'a aucun CORS, cf. `bordereauCoverDocxExport.buildLetterheadParagraph`).
 * @param options - voir {@link OptionsDePose}. Omis = pose strictement additive.
 */
export async function poserLEnTeteDuCabinet(
  doc: DocumentApiEnTete | null,
  entree: EnTeteAPoser,
  options: OptionsDePose = {},
): Promise<ResultatDeLEnTete> {
  const { settings, imageBlob } = entree;
  if (!settings?.enabled) return 'desactive';
  if (!settings.image || !imageBlob) return 'sans-image';

  const sections = doc?.sections;
  const headerFooters = doc?.headerFooters;
  if (
    !sections?.list ||
    !sections.setTitlePage ||
    !headerFooters?.list ||
    !headerFooters.parts?.create ||
    !headerFooters.refs?.set ||
    !doc?.create?.image
  ) {
    return 'api-indisponible';
  }

  const section = lirePremiereAdresseDeSection(await sections.list());
  if (!section) return 'refus-du-moteur';

  const slot: AdresseDuSlotDePremierePage = {
    kind: 'headerFooterSlot',
    section,
    headerFooterKind: 'header',
    variant: 'first',
  };

  const enTeteExistant = lireLEnTeteDePremierePage(await headerFooters.list({ kind: 'header' }), section);
  if (enTeteExistant && !options.remplacerLEnTeteExistant) return 'en-tete-deja-present';
  // `refs.clear` n'est exigée QUE lorsqu'il y a réellement quelque chose à
  // détacher — la placer dans la garde générale ferait renoncer une pose
  // strictement additive sur un runtime parfaitement capable de la faire. Et un
  // détachement impossible est bien une SURFACE absente, pas un refus passager :
  // « réessayez dans un instant » enverrait l'avocat cliquer en boucle.
  if (enTeteExistant && !headerFooters.refs.clear) return 'api-indisponible';

  // Un document Word avec en-tête uniquement sur la variante `default` (toutes les
  // pages) n'a aucune référence `first` explicite : `lireLEnTeteDePremierePage` le
  // juge « sans en-tête ». Si on continuait, `setTitlePage` activerait la première
  // page spécifique et le logo du cabinet remplacerait le papier à en-tête de
  // l'avocat en page 1 — sans confirmation, et hors de portée de l'invariant
  // d'idempotence habituel.
  // `resolve` lève l'ambiguïté : un statut `inherited` signifie que la première
  // page reçoit effectivement un en-tête via la variante `default`. On renonce
  // alors — uniquement en pose ADDITIVE. Le remplacement explicitement confirmé
  // (`remplacerLEnTeteExistant`) n'est pas concerné : l'avocat a déjà accepté.
  if (!enTeteExistant && !options.remplacerLEnTeteExistant && headerFooters.resolve) {
    const resolutionDuSlot = await headerFooters.resolve({ target: slot });
    if (lireLeStatutDeResolution(resolutionDuSlot) === 'inherited') return 'en-tete-deja-present';
  }

  // La géométrie ET la lecture de l'image AVANT toute mutation, y compris avant
  // le retrait de l'en-tête existant : une image illisible ne doit jamais laisser
  // derrière elle un `titlePg` levé, une partie d'en-tête vide, et surtout pas un
  // document dont on vient de retirer l'en-tête pour ne rien mettre à la place.
  let taille: { widthMm: number; heightMm: number } | null = null;
  let dataUri: string;
  try {
    const bitmap = await createImageBitmap(imageBlob);
    taille = fitBordereauCoverLetterhead({
      naturalWidthPx: bitmap.width,
      naturalHeightPx: bitmap.height,
      requestedWidthMm: settings.image.widthMm ?? null,
      availableWidthMm: LARGEUR_UTILE_DU_CANVAS_MM,
    });
    bitmap.close();
    // `versDataUri` REJETTE sur une erreur `FileReader`. Hors de ce `try`, elle
    // faisait mentir l'invariant « ne lève jamais » du module : l'appelant
    // recevait une promesse rejetée là où il attend un motif d'abandon.
    dataUri = await versDataUri(imageBlob);
  } catch {
    return 'image-illisible';
  }
  if (!taille) return 'image-illisible';

  // Le retrait n'a lieu qu'ici : tout ce qui pouvait échouer sans toucher au
  // document a déjà été fait.
  if (enTeteExistant && !(await retirerLEnTeteDePremierePage(headerFooters, slot, enTeteExistant.refId))) {
    return 'refus-du-moteur';
  }

  // `titlePg` d'abord : c'est lui qui fait exister la variante « première page ».
  // Levé avant l'écriture, un échec de la suite laisse un document intact à l'œil
  // (aucune variante `first` n'est référencée, donc rien ne s'affiche).
  if (!receiptEstEnSucces(await sections.setTitlePage({ target: section, enabled: true }))) return 'refus-du-moteur';

  const refId = lireLeRefIdCree(await headerFooters.parts.create({ kind: 'header' }));
  if (!refId) return 'refus-du-moteur';

  const rattachement = await headerFooters.refs.set({ target: slot, refId });
  if (!receiptEstEnSucces(rattachement)) return 'refus-du-moteur';

  const story: StoryDEnTete = { kind: 'story', storyType: 'headerFooterPart', refId };
  const pose = await doc.create.image({
    in: story,
    src: dataUri,
    alt: "En-tête du cabinet",
    size: {
      width: Math.round(taille.widthMm * MM_TO_PT),
      height: Math.round(taille.heightMm * MM_TO_PT),
      unit: 'pt',
    },
  });
  if (!receiptEstEnSucces(pose)) return 'refus-du-moteur';

  // La finition est cosmétique et le logo est DÉJÀ dans le document : une
  // exception venue d'ici (une surface absente rendrait `undefined`, un moteur
  // récalcitrant pourrait lever) transformerait une pose réussie en message
  // d'échec sous les yeux d'un avocat qui voit son en-tête à l'écran.
  try {
    await finirLaMiseEnPage(doc, story, settings.alignment);
  } catch {
    // Rien à rattraper : un logo un peu bas ou mal calé reste très au-dessus
    // d'un canvas sans en-tête (même arbitrage que `finirLaMiseEnPage`).
  }
  return enTeteExistant ? 'remplace' : 'pose';
}

/**
 * Le geste de l'avocat : « Appliquer l'en-tête du cabinet », rétroactif.
 *
 * **Une seule entrée, pas de sonde séparée.** Un `sonder()` suivi d'un
 * `poser()` ouvrirait une fenêtre entre la question et l'acte, et surtout
 * dupliquerait l'ordre des gardes : un cabinet sans en-tête configuré
 * déclencherait une confirmation avant d'apprendre qu'il n'y a rien à poser.
 * Ici, la première passe EST la tentative additive — celle qui ne détruit
 * jamais rien. Elle ne demande de confirmation que sur le seul motif qui la
 * justifie : un en-tête déjà en place, dont le module ne sait pas de qui il
 * vient (`lireLEnTeteDePremierePage`).
 *
 * @param options.confirmeParLAvocat - `true` uniquement après un « Remplacer »
 *   explicite. C'est la seule chose qui autorise une destruction.
 */
/**
 * Ce que la première page porte, relu sur le document.
 *
 * `'aucun'` = aucune référence `first` explicite ET aucun en-tête hérité de la
 * variante `default`. `'herite'` est distinct de `'pose'` parce qu'un en-tête
 * hérité n'appartient à aucun slot de première page : on ne peut ni le retirer
 * ni le remplacer par ce chemin sans toucher toutes les pages du document.
 * `null` = le moteur n'expose pas de quoi répondre — « je ne sais pas », qui ne
 * doit jamais se lire comme « il n'y en a pas ».
 */
export type EtatDeLEnTete = 'pose' | 'herite' | 'aucun' | null;

/**
 * Relit ce que porte la première page, sans rien modifier.
 *
 * Le geste de l'avocat n'en a pas besoin — sa première passe EST la sonde, et
 * un `sonder()` puis `poser()` ouvrirait une fenêtre entre la question et
 * l'acte. Un exécuteur d'agent, lui, doit rendre un accusé qui dit ce que le
 * document porte APRÈS son écriture : c'est la postcondition, et elle ne peut
 * pas être une seconde tentative de pose.
 */
export async function lireLEtatDeLEnTete(doc: DocumentApiEnTete | null): Promise<EtatDeLEnTete> {
  const sections = doc?.sections;
  const headerFooters = doc?.headerFooters;
  if (!sections?.list || !headerFooters?.list) return null;

  const section = lirePremiereAdresseDeSection(await sections.list());
  if (!section) return null;

  if (lireLEnTeteDePremierePage(await headerFooters.list({ kind: 'header' }), section)) return 'pose';

  if (headerFooters.resolve) {
    const slot: AdresseDuSlotDePremierePage = {
      kind: 'headerFooterSlot',
      section,
      headerFooterKind: 'header',
      variant: 'first',
    };
    if (lireLeStatutDeResolution(await headerFooters.resolve({ target: slot })) === 'inherited') return 'herite';
  }
  return 'aucun';
}

/**
 * Retire l'en-tête de première page sans en poser aucun autre.
 *
 * ⚠️ **Ce chemin n'existait pas avant le verbe d'agent** (2026-09-06), et son
 * absence était un choix, pas un oubli : côté avocat, retirer un en-tête sans
 * en poser un autre ne correspondait à aucun bouton. `retirerLEnTeteDePremierePage`
 * n'était atteignable qu'au milieu d'un remplacement, immédiatement suivi de la
 * re-pose. L'agent, lui, peut recevoir « enlève l'en-tête » comme une demande
 * entière.
 *
 * Le refus sur `'herite'` est load-bearing : un en-tête hérité de la variante
 * `default` s'affiche sur TOUTES les pages, et le détacher du slot `first` — qui
 * ne le porte pas — ne retirerait rien tout en rendant `success`. Le mensonge
 * serait silencieux, et l'agent annoncerait à l'avocat un retrait qui n'a pas eu
 * lieu.
 */
export async function retirerLEnTeteDuCabinet(doc: DocumentApiEnTete | null): Promise<ResultatDuRetrait> {
  const sections = doc?.sections;
  const headerFooters = doc?.headerFooters;
  if (!sections?.list || !headerFooters?.list || !headerFooters.refs?.clear) return 'api-indisponible';

  const section = lirePremiereAdresseDeSection(await sections.list());
  if (!section) return 'refus-du-moteur';

  const slot: AdresseDuSlotDePremierePage = {
    kind: 'headerFooterSlot',
    section,
    headerFooterKind: 'header',
    variant: 'first',
  };
  const existant = lireLEnTeteDePremierePage(await headerFooters.list({ kind: 'header' }), section);
  if (!existant) {
    if (headerFooters.resolve
      && lireLeStatutDeResolution(await headerFooters.resolve({ target: slot })) === 'inherited') {
      return 'en-tete-herite';
    }
    return 'aucun-en-tete';
  }

  return await retirerLEnTeteDePremierePage(headerFooters, slot, existant.refId) ? 'retire' : 'refus-du-moteur';
}

/** Pourquoi le retrait n'a pas eu lieu, ou qu'il a eu lieu. */
export type ResultatDuRetrait =
  | 'retire'
  | 'aucun-en-tete'
  | 'en-tete-herite'
  | 'api-indisponible'
  | 'refus-du-moteur';

export async function appliquerLEnTeteDuCabinet(
  doc: DocumentApiEnTete | null,
  entree: EnTeteAPoser,
  options: { readonly confirmeParLAvocat: boolean },
): Promise<GesteDEnTete> {
  if (options.confirmeParLAvocat) {
    const resultat = await poserLEnTeteDuCabinet(doc, entree, { remplacerLEnTeteExistant: true });
    return { etat: 'termine', resultat };
  }

  const resultat = await poserLEnTeteDuCabinet(doc, entree);
  if (resultat === 'en-tete-deja-present') return { etat: 'confirmation-requise' };
  return { etat: 'termine', resultat };
}

/**
 * Issue du geste : soit il est allé au bout, soit il attend l'accord de
 * l'avocat pour écraser ce qui est en place. Une VALEUR, comme les motifs
 * d'abandon — l'appelant décide de la suite, il n'a rien à rattraper.
 */
export type GesteDEnTete =
  | { readonly etat: 'confirmation-requise' }
  | { readonly etat: 'termine'; readonly resultat: ResultatDeLEnTete };

/** Ce que l'écran doit dire, et sur quel ton. */
export interface MessageDEnTete {
  readonly ton: 'succes' | 'information' | 'erreur';
  /** Phrase destinée à l'avocat : français, sans terme technique, sans émoji. */
  readonly texte: string;
  /**
   * `true` quand la suite se joue dans les réglages du cabinet — l'écran y
   * ajoute alors un raccourci vers `/personnalisation#charte/en-tete`. C'est un
   * FAIT sur l'issue, pas une URL : le module ne connaît pas les routes.
   */
  readonly versLesReglages: boolean;
}

/**
 * Traduit chaque issue en une phrase que l'avocat peut suivre.
 *
 * La règle, pour toute ligne ajoutée ici : dire **ce qui s'est passé** et
 * **quoi faire ensuite**. « L'en-tête n'a pas pu être posé » seul oblige à
 * deviner ; c'est exactement ce que la doctrine d'erreurs du dépôt interdit.
 * Aucun code de retour, aucun nom d'API : ces mots vont dans un toast, sous
 * les yeux d'un avocat.
 */
export function messageDeLEnTete(resultat: ResultatDeLEnTete): MessageDEnTete {
  switch (resultat) {
    case 'pose':
      return { ton: 'succes', texte: "L'en-tête du cabinet a été ajouté en première page.", versLesReglages: false };
    case 'remplace':
      return {
        ton: 'succes',
        texte: "L'en-tête de la première page a été remplacé par celui du cabinet.",
        versLesReglages: false,
      };
    case 'desactive':
      return {
        ton: 'information',
        texte:
          "Aucun en-tête n'est actif pour votre cabinet. Déposez votre papier à en-tête et activez-le dans Personnalisation, onglet Charte graphique.",
        versLesReglages: true,
      };
    case 'sans-image':
      return {
        ton: 'information',
        texte:
          "L'en-tête de votre cabinet n'a pas d'image. Déposez le fichier de votre papier à en-tête dans Personnalisation, onglet Charte graphique.",
        versLesReglages: true,
      };
    case 'en-tete-deja-present':
      // Inatteignable par le geste de l'avocat (`appliquerLEnTeteDuCabinet` en
      // fait une demande de confirmation), mais atteignable si un en-tête
      // apparaît entre la question et l'accord. Le message dit alors la vérité :
      // le geste n'a rien fait, et le refaire proposera le remplacement.
      return {
        ton: 'information',
        texte:
          "Ce document a déjà un en-tête de première page. Relancez l'action pour le remplacer par celui du cabinet.",
        versLesReglages: false,
      };
    case 'api-indisponible':
      return {
        ton: 'erreur',
        texte:
          "Ce document ne permet pas de modifier son en-tête. Enregistrez-le au dossier, rouvrez-le, puis réessayez.",
        versLesReglages: false,
      };
    case 'image-illisible':
      return {
        ton: 'erreur',
        texte:
          "L'image de votre en-tête n'a pas pu être lue. Vérifiez qu'il s'agit d'un PNG ou d'un JPEG valide dans Personnalisation, onglet Charte graphique.",
        versLesReglages: true,
      };
    case 'refus-du-moteur':
      return {
        ton: 'erreur',
        texte:
          "L'en-tête n'a pas pu être posé dans ce document. Réessayez dans un instant ; si le refus persiste, enregistrez le document au dossier puis rouvrez-le.",
        versLesReglages: false,
      };
  }
}

/**
 * Deux finitions, toutes deux facultatives — mais plus silencieuses : un refus
 * du moteur est désormais journalisé avec son code. Elles l'étaient, et c'est
 * précisément ce qui a permis à l'alignement de se perdre sans que rien ne le
 * signale (2026-08-24). Un refus ne fait toujours pas échouer la pose ; il se
 * dit, simplement.
 *
 *
 *  1. `parts.create` fabrique une partie qui contient DÉJÀ un paragraphe vide, et
 *     `create.image` en ajoute un second pour l'image — d'où une ligne blanche
 *     au-dessus du logo (constatée dans le `word/header1.xml` réexporté par la
 *     sonde). On retire le paragraphe vide de tête.
 *  2. L'alignement choisi par l'avocat, appliqué au paragraphe du logo.
 *
 * Aucune des deux ne conditionne le succès : un logo un peu bas ou mal calé
 * reste très au-dessus d'un canvas sans en-tête.
 */
async function finirLaMiseEnPage(
  doc: DocumentApiEnTete,
  story: StoryDEnTete,
  alignement: LetterheadAlignment,
): Promise<void> {
  const blocs = lireLesBlocs(await doc.blocks?.list?.({ in: story, includeText: true }));
  const blocDuLogo = blocs[blocs.length - 1];
  if (!blocDuLogo) return;

  const dernier = lireLAdresseDunBloc(blocDuLogo, story);
  if (dernier && doc.format?.paragraph?.setAlignment) {
    const recu = await doc.format.paragraph.setAlignment({ target: dernier, alignment: alignement });
    // Le Document API ne JETTE pas sur une cible introuvable : il rend un reçu en
    // échec. Sans cette ligne, une adresse mal formée perd l'alignement en
    // silence — le défaut du 2026-08-24 est resté invisible jusqu'à ce qu'un
    // avocat compare son aperçu de réglages au document à l'écran.
    const echec = lireLeCodeDEchec(recu);
    if (echec !== null && echec !== 'NO_OP') {
      console.warn('[canvasLetterhead] alignement du logo refusé', { code: echec, alignement, cible: dernier });
    }
  }

  const premierBloc = blocs.length >= 2 ? blocs[0] : undefined;
  if (!premierBloc) return;
  const apercu = premierBloc.textPreview ?? premierBloc.text;
  const premierEstVide = apercu === undefined || apercu === null || apercu === '';
  const premier = lireLAdresseDunBloc(premierBloc, story);
  if (premierEstVide && premier && doc.blocks?.delete) {
    const recu = await doc.blocks.delete({ target: premier });
    const echec = lireLeCodeDEchec(recu);
    if (echec !== null && echec !== 'NO_OP') {
      console.warn('[canvasLetterhead] retrait du paragraphe vide de tête refusé', { code: echec, cible: premier });
    }
  }
}
